import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, ShieldAlert, CheckCircle2, ArrowLeft, Send, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

const KINDS = [
  { value: "scam", label: "🚨 Arnaque / phishing" },
  { value: "outdated", label: "📅 Information obsolète" },
  { value: "wrong_info", label: "❌ Information incorrecte" },
  { value: "broken_link", label: "🔗 Lien cassé" },
  { value: "other", label: "💬 Autre" },
];

export default function Signaler() {
  useSeo({
    title: "Signaler une arnaque ou une info obsolète — Student Hub",
    description:
      "Aide-nous à protéger la communauté étudiante en France. Signale une arnaque au logement, une info obsolète ou un lien cassé sur Student Hub. Anonyme et gratuit.",
    canonical: "https://studenthubfrance.fr/signaler",
  });

  const [kind, setKind] = useState("scam");
  const [url, setUrl] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      toast.error("Merci de décrire le problème (10 caractères minimum).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/reports/scam", {
        kind,
        url: url.trim() || null,
        message: message.trim(),
        email: email.trim() || null,
      });
      setDone(true);
      toast.success("Merci ! Ton signalement a été transmis.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur : réessaie dans un instant");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="signaler-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-indigo-500 text-white flex items-center justify-center">
              <GraduationCap size={20} />
            </div>
            <span className="font-display font-bold text-lg text-slate-900">Student Hub</span>
          </Link>
          <Link
            to="/sources-officielles"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 hidden sm:inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} /> Sources officielles
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium mb-4">
            <ShieldAlert size={14} />
            Signalement communautaire
          </div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900 tracking-tight">
            Signaler une arnaque ou une info obsolète
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Tu as repéré une <strong>arnaque au logement</strong>, une info dépassée, un lien cassé ou une
            information incorrecte sur Student Hub ? Merci de nous prévenir : chaque signalement aide à protéger la
            communauté étudiante en France. Anonyme, gratuit, et pris au sérieux.
          </p>
        </div>

        {done ? (
          <div
            className="bg-white rounded-2xl border border-emerald-200 p-8 text-center"
            data-testid="signaler-thanks"
          >
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 size={30} className="text-emerald-600" />
            </div>
            <h2 className="font-display font-bold text-2xl text-slate-900">Signalement envoyé — merci !</h2>
            <p className="text-slate-600 mt-2">
              Notre équipe examinera ta contribution sous 48 h. Si nécessaire, nous mettrons à jour l'article ou
              retirerons le contenu concerné.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => {
                  setDone(false);
                  setKind("scam");
                  setUrl("");
                  setMessage("");
                  setEmail("");
                }}
                variant="outline"
                data-testid="signaler-new-btn"
              >
                Envoyer un autre signalement
              </Button>
              <Link to="/">
                <Button data-testid="signaler-home-btn">Retour à l'accueil</Button>
              </Link>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-5"
            data-testid="signaler-form"
          >
            <div>
              <Label className="text-slate-900 font-medium">Type de problème</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {KINDS.map((k) => (
                  <button
                    type="button"
                    key={k.value}
                    onClick={() => setKind(k.value)}
                    className={`px-4 py-3 text-left rounded-xl border transition-colors text-sm ${
                      kind === k.value
                        ? "border-indigo-500 bg-indigo-50 text-indigo-700 font-medium"
                        : "border-slate-200 hover:border-slate-300 text-slate-700"
                    }`}
                    data-testid={`signaler-kind-${k.value}`}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="signaler-url" className="text-slate-900 font-medium">
                URL concernée <span className="text-slate-400 font-normal">(optionnel)</span>
              </Label>
              <Input
                id="signaler-url"
                type="url"
                placeholder="https://example.com/annonce-frauduleuse"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="signaler-url-input"
                className="mt-1.5"
              />
              <p className="text-xs text-slate-500 mt-1">
                Lien direct vers l'annonce, la page ou l'article problématique.
              </p>
            </div>

            <div>
              <Label htmlFor="signaler-message" className="text-slate-900 font-medium">
                Décris le problème <span className="text-rose-500">*</span>
              </Label>
              <Textarea
                id="signaler-message"
                placeholder="Ex : Ce logement est proposé à 250 €/mois mais le propriétaire demande un virement à l'étranger avant visite. Le RIB fourni est aux Pays-Bas alors que l'annonce dit Lyon."
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                minLength={10}
                maxLength={4000}
                data-testid="signaler-message-input"
                className="mt-1.5"
              />
              <p className="text-xs text-slate-500 mt-1">
                Plus tu es précis(e), plus vite on pourra agir. {message.length}/4000
              </p>
            </div>

            <div>
              <Label htmlFor="signaler-email" className="text-slate-900 font-medium">
                Ton email <span className="text-slate-400 font-normal">(optionnel — pour un retour)</span>
              </Label>
              <Input
                id="signaler-email"
                type="email"
                placeholder="toi@exemple.fr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signaler-email-input"
                className="mt-1.5"
              />
              <p className="text-xs text-slate-500 mt-1">
                Utilisé uniquement pour te répondre. Aucun spam, aucune newsletter, jamais.
              </p>
            </div>

            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info size={16} className="text-blue-600 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-900 leading-relaxed">
                Pour un signalement <strong>officiel d'arnaque</strong>, contacte aussi{" "}
                <a
                  href="https://signal.conso.gouv.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                >
                  SignalConso.gouv.fr
                </a>{" "}
                (DGCCRF) ou{" "}
                <a
                  href="https://www.cybermalveillance.gouv.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                >
                  Cybermalveillance.gouv.fr
                </a>{" "}
                pour un phishing.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full gap-2"
              disabled={busy}
              data-testid="signaler-submit-btn"
            >
              <Send size={16} />
              {busy ? "Envoi…" : "Envoyer le signalement"}
            </Button>
          </form>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white py-8 mt-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Student Hub — Signalement traité en 48 h ouvrées.</p>
        </div>
      </footer>
    </div>
  );
}
